import React, { Component, ReactNode } from 'react';

import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import {
    Event as SentryEvent,
    EventHint,
    init,
} from '@sentry/react';
import { AxiosError } from 'axios';
import debounce from 'lodash/debounce';

import { AppStore, AuthSettingsStore } from '@/stores';
import { captureErrorForSentry, redactSensitiveData } from '../utils';
import { getSentryCtx } from '../utils/sentryAdapter';
import { IS_LOCAL_ENV } from '@/config/env';

import FallbackUI from './FallbackUI';

const DENY_URLS = [
    /dev-app\.specterx\.com/i,
    /staging-app\.specterx\.com/i,
];

const DEBOUNCE_TIMEOUT_MS = 100;

/**
 * Strip dynamic path segments (UUIDs, numeric IDs, query strings) from an API
 * URL so the normalized endpoint can appear in Sentry titles without creating
 * a unique issue per request.
 *
 * Examples:
 *   /access/files/3e4f-…/permissions  →  access/files/:id/permissions
 *   /access/files/multiple?files_ids=… →  access/files/multiple
 */
const normalizeRoute = (url?: string): string => {
    if (!url) {
        return 'unknown endpoint';
    }
    return url
        .replace(/^https?:\/\/[^/]+/, '')  // strip origin
        .split('?')[0]                       // drop query string
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
        .replace(/\/\d+(?=\/|$)/g, '/:id')  // numeric IDs
        .replace(/^\//, '');                 // leading slash
};

const tryShowDebugOverlay = debounce((error: Error): void => {
    if (IS_LOCAL_ENV) {
        const ErrorOverlay = customElements.get('vite-error-overlay');
        if (ErrorOverlay) {
            document.body.appendChild(new ErrorOverlay(error));
        }
    }
}, DEBOUNCE_TIMEOUT_MS);

const applyIssuePresentation = (event: SentryEvent, hint: EventHint): void => {
    const ctx = getSentryCtx(hint.originalException);
    const exception = event.exception?.values?.[0];
    if (!ctx || !exception) {
        return;
    }

    const { context, kind, httpStatus, cognitoErrorCode } = ctx;
    const originalClass = exception.type ?? 'Error';
    let value: string;
    let fingerprint: string[];

    switch (kind) {
    case 'api': {
        const status = httpStatus ?? 'unknown';
        const axiosConfig = (hint.originalException as AxiosError)?.config;
        const method = axiosConfig?.method?.toUpperCase() ?? '';
        const route = normalizeRoute(axiosConfig?.url);
        value = [method, route, `[${status}]`].filter(Boolean).join(' ');
        fingerprint = ['api', context, status];
        break;
    }
    case 'cognito': {
        const code = cognitoErrorCode ?? 'unknown';
        value = `Cognito ${code}`;
        fingerprint = ['cognito', context, code];
        break;
    }
    case 'non-error':
        value = 'Non-Error exception';
        fingerprint = [context, 'non-error'];
        break;
    default:
        value = originalClass;
        fingerprint = [context, originalClass];
    }

    /* eslint-disable no-param-reassign */
    exception.type = context;
    exception.value = value;
    event.fingerprint = fingerprint;
    // Stamp searchable tags on the event (in addition to the title rewrite)
    event.tags = {
        ...(event.tags ?? {}),
        errorContext: context,
        errorKind: kind,
        ...(httpStatus ? { httpStatus } : {}),
        ...(cognitoErrorCode ? { cognitoErrorCode } : {}),
    };
    /* eslint-enable no-param-reassign */
};

const beforeSendSentry = (event: SentryEvent, hint: EventHint): SentryEvent => {
    if ((hint.originalException as AxiosError)?.config) {
        const { config } = hint.originalException as AxiosError;
        const date = new Date();
        const timestamp = date.getTime();
        if (config.data) {
            event.breadcrumbs.push({
                timestamp,
                category: 'Request Data',
                data: redactSensitiveData(config.data),
                level: 'info',
            });
        }

        event.breadcrumbs.push({
            timestamp,
            category: 'Path',
            message: config.url,
            level: 'info',
        });
    }

    applyIssuePresentation(event, hint);

    // Fallback: normalize raw Axios errors that bypassed captureErrorForSentry
    // (direct captureException calls without errorContext). These still arrive
    // with config so we can produce a stable title from method + route + status.
    const exception = event.exception?.values?.[0];
    const axios = hint.originalException as AxiosError;
    if (!event.tags?.errorContext && exception && axios?.config) {
        const status = axios.response?.status ?? 'unknown';
        const method = axios.config?.method?.toUpperCase() ?? '';
        const route = normalizeRoute(axios.config?.url);
        /* eslint-disable no-param-reassign */
        exception.value = [method, route, `[${status}]`].filter(Boolean).join(' ');
        event.fingerprint = ['axios', method, route, String(status)];
        event.tags = { ...(event.tags ?? {}), httpStatus: String(status) };
        /* eslint-enable no-param-reassign */
    }

    return event;
};

interface ErrorsBoundaryProps {
    appStore?: AppStore;
    authSettingsStore?: AuthSettingsStore;
    children: ReactNode;
}

interface ErrorsBoundaryPropsState {
    hasError: boolean;
}

@inject('appStore', 'authSettingsStore')
@observer
class ErrorsBoundary extends Component<ErrorsBoundaryProps, ErrorsBoundaryPropsState> {
    private sentryEnabled: boolean;

    constructor(props: ErrorsBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorsBoundaryPropsState {
        return { hasError: true };
    }

    componentDidMount(): void {
        const { authSettingsStore } = this.props;

        // Register unhandledrejection BEFORE Sentry init so our listener fires
        // first and can attach context (via captureErrorForSentry) to the error
        // object before Sentry's own GlobalHandlers integration captures it.
        window.addEventListener('unhandledrejection', (event) => {
            const { reason } = event;
            captureErrorForSentry(reason, 'ErrorsBoundary.unhandledrejection');
            event.preventDefault();
        });

        if (!authSettingsStore.HAS_API_PROXY && !IS_LOCAL_ENV) {
            this.initSentry();
        }
        window.addEventListener('error', (event) => {
            const { error } = event;
            tryShowDebugOverlay(error);
        });

        reaction<boolean>(
            () => authSettingsStore.HAS_API_PROXY,
            (hasAPIProxy) => {
                const shouldEnable = !hasAPIProxy && !IS_LOCAL_ENV && !this.sentryEnabled;
                if (shouldEnable) {
                    this.initSentry();
                }
            },
        );
    }

    componentDidCatch(error: Error): void {
        const { appStore } = this.props;
        const { errorStatus, setErrorStatus } = appStore;
        if (IS_LOCAL_ENV) {
            // eslint-disable-next-line no-console
            console.log('sentry catch error', error);
        }

        if (!errorStatus.hasError) {
            captureErrorForSentry(error, 'ErrorsBoundary.componentDidCatch');
            setErrorStatus(true, 'application');
            tryShowDebugOverlay(error);
        }
    }

    private initSentry(): void {
        init({
            environment: import.meta.env.VITE_STAGE,
            dsn: 'https://105096a748f54f0ab4f29164c83d234d@sentry.io/1840560',
            attachStacktrace: true,
            release: 'specterx@0.2.0',
            beforeSend: beforeSendSentry,
            denyUrls: DENY_URLS,
            autoSessionTracking: true,
            beforeBreadcrumb: (breadcrumb, hint) => {
                if (breadcrumb.category === 'xhr') {
                    /* eslint-disable no-param-reassign */
                    breadcrumb.data.url = breadcrumb.data.url.replace('auth', 'a*th');
                    // hint.xhr is a whole XHR object that you can use to modify breadcrumb
                    breadcrumb.data.responseText = (hint.xhr as XMLHttpRequest).response;
                    /* eslint-enable no-param-reassign */
                }

                return breadcrumb;
            },
            sampleRate: 0.5,
        });
        this.sentryEnabled = true;
    }

    render(): JSX.Element | ReactNode {
        const { children } = this.props;
        const { hasError } = this.state;

        return hasError ? <FallbackUI /> : children;
    }
}

export default ErrorsBoundary;
